import { InputLabel, MenuItem, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo, useEffect } from 'react';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';
import { ItemVM } from 'shell/view-models/item';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';

interface ArticleListProps {
  disabled?: boolean;
  value: string;
  onChange: (value: ArticleVM | null) => void;
  error?: boolean;
  idReason: string;
}

export type ArticleVM = ItemVM<MotArtCitizenship> & {
  subEvent?: string;
};

const ArticleListComponent = ({
  disabled,
  onChange,
  value,
  error,
  idReason,
}: ArticleListProps): React.ReactElement => {
  const [articles, getArticles] = useHttpFetch<
    HttpResponse<Array<MotArtCitizenship>>,
    Array<ArticleVM>
  >(`${__PERSON_NEW_API_URL__}/leggi-sulla-cittadinanza/motivi`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response) => {
      return (response.data.payload || []).map((element) => {
        const key = element?.key;

        return {
          id: `${key?.anno || ''}.${key?.articolo || ''}.${key?.comma || ''}.${key?.numero || ''}.${
            key?.parte || ''
          }.${key?.punto || ''}.${key?.subEvento || ''}`,
          value: `${element?.descrizione || ''} ${
            element.descrizioneBreve ? `(${element.descrizioneBreve})` : ''
          }`,
          el: element,
          subEvent: element?.key?.subEvento || '',
        } as ArticleVM;
      }) as Array<ArticleVM>;
    },
  });

  useEffect(() => {
    if (idReason) {
      getArticles({
        url: `/${idReason}`,
      });
    }
  }, [idReason]);

  const onChangeSelect = useEventCallback((event: SelectChangeEvent<string>) => {
    // return all element
    const id = event.target.value;

    const valueRes: ArticleVM | null =
      ((articles.data || []) as Array<ArticleVM>).find((item) => item.id === id) || null;

    onChange(valueRes);
  });

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id="article-label">
        Articolo
      </InputLabel>
      <PmiSelect
        labelId="article-label"
        id="article"
        name="article"
        value={value}
        label="Articolo"
        onChange={onChangeSelect}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {((articles.data || []) as Array<ArticleVM>).map((article: ArticleVM) => (
          <MenuItem key={article.id} value={article.id}>
            {article.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ArticleListComponent.displayName = 'article list component';

const ArticleList = memo(ArticleListComponent);

export default ArticleList as typeof ArticleListComponent;
